import { MENTION_TYPE, Sentiment } from "@/constants";
import { MentionCard, MentionV4 } from "@/types/MentionCard";
import { Button } from "@components/ui/button";
import { Collapsible, CollapsibleContent } from "@components/ui/collapsible";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  EyeIcon,
  LanguagesIcon,
  MoreVerticalIcon,
  Trash2Icon,
} from "lucide-react";
import MentionCollapsibleTrigger from "./MentionCollapsibleTrigger";
import Karaoke from "./Karaoke";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import { useMutation } from "@tanstack/react-query";
import { markIrrelevant } from "@/api/endpoints/searches";
import { toast } from "sonner";
import StaffBadge from "@components/StaffBadge";
import AccessControl, { ACCESS_LEVELS } from "@components/AccessControl";
import { translate, TranslationResponse } from "@/api/endpoints/translate";
import { Channel } from "@/types/Channel";
import { useI18n } from "@/contexts/i18nContext";
import { postTrackingData } from "@/api/endpoints/track";
import { EVENT } from "@/tracking_events";
import { SENTIMENT } from "@/utils/sentimentOptions";
import { i18n } from "@lingui/core";
import { setSentiment } from "@/api/endpoints/episodes";
import SimilarMentions from "./SimilarMentions";
import { useReadOnly } from "../context";

type MentionItemProps = {
  onAfterTranslation: (
    translatedText: TranslationResponse["translated_text"]
  ) => void;
  groupedTimesAndWords: MentionCard["grouped_times_and_words"];
  onAfterChangeSentiment: (
    newSentiment: Sentiment,
    newAggregatedSentiment: Sentiment
  ) => void;
  channelLanguage: Channel["language"];
  onRevertTranslation: () => void;
  onAfterDelete: () => void;
  mentionsCount: number;
  defaultOpen?: boolean;
  mention: MentionV4;
  index: number;
};

export function MentionItem({
  onAfterChangeSentiment,
  groupedTimesAndWords,
  onRevertTranslation,
  defaultOpen = false,
  onAfterTranslation,
  channelLanguage,
  onAfterDelete,
  mentionsCount,
  mention,
  index,
}: MentionItemProps) {
  const { t } = useLingui();
  const { language } = useI18n();
  const readOnly = useReadOnly();

  // Helpers
  const isAudioMention = typeof mention.timing === "number";
  const isDescriptionMention =
    mention.context === MENTION_TYPE.EPISODE_DESCRIPTION;

  const SentimentIcon = mention.sentiment && SENTIMENT[mention.sentiment]?.icon;

  // Server state
  const { mutate: mutateMarkAsIrrelevant } = useMutation({
    mutationFn: () => markIrrelevant([mention.id]),
    onSuccess: (res) => {
      if (!res.ok) {
        throw new Error();
      }

      toast.success(t`Omnämnande borttaget`);

      onAfterDelete();
    },
    onError: () => {
      toast.error(t`Misslyckades med att ta bort omnämnandet`);
    },
  });

  const { mutate: mutateTranslate } = useMutation({
    mutationFn: () =>
      translate({
        text: isDescriptionMention
          ? mention.trimmed_display_text
          : mention.display_text,
        target_language: language,
      }),
    onSuccess: (res) => {
      if (!res.ok) {
        throw new Error();
      }

      postTrackingData(EVENT.MENTION_TRANSLATED, {
        from_language: res.data.original_language,
        to_language: res.data.target_language,
      });

      onAfterTranslation(res.data.translated_text);
    },
    onError: () => {
      toast.error(t`Lyckades inte översätta omnämnande`);
    },
  });

  const { mutate: mutateSetSentiment } = useMutation({
    mutationFn: (newSentiment: Sentiment) =>
      setSentiment([mention.public_id], newSentiment),
    onSuccess: (res, newSentiment) => {
      if (!res.ok) {
        throw new Error();
      }

      onAfterChangeSentiment(newSentiment, res.data.aggregated_sentiment);
    },
    onError: () => {
      toast.error(t`Misslyckades med att ändra sentiment`);
    },
  });

  return (
    <Collapsible
      key={mention.id}
      defaultOpen={defaultOpen}
      className="group/collapsible w-full"
    >
      <MentionCollapsibleTrigger
        displayIndex={index + 1}
        isDescriptionMention={isDescriptionMention}
        indicesMetadata={mention.indices_metadata}
        mentionsCount={mentionsCount}
      />

      <CollapsibleContent className="pl-3 pr-1 py-3 pt-0 bg-muted-secondary rounded-md group-data-open/collapsible:rounded-t-none flex gap-1">
        <div className="flex-1 flex flex-col gap-2">
          {isAudioMention &&
          !mention.translated &&
          groupedTimesAndWords &&
          mention.indices_metadata ? (
            <Karaoke
              indicesMetadata={mention.indices_metadata}
              groupedTimesAndWords={groupedTimesAndWords}
              displayText={mention.display_text}
            />
          ) : (
            <p
              data-testid="mention-text-content"
              className="mention text-sm leading-6.5 text-balance highlight-match"
              dangerouslySetInnerHTML={{
                __html:
                  mention.translated && mention.translated_text
                    ? mention.translated_text
                    : isDescriptionMention
                      ? mention.trimmed_display_text
                      : mention.display_text,
              }}
            />
          )}

          {mention.similar_mentions.length ? (
            <SimilarMentions similarMentions={mention.similar_mentions} />
          ) : null}
        </div>

        {!readOnly && (
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  data-testid="mention-item-menu-toggler"
                  size="icon-sm"
                  variant="ghost"
                />
              }
            >
              <MoreVerticalIcon />
              <span className="sr-only">
                <Trans>Mer</Trans>
              </span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {channelLanguage !== language ? (
                <DropdownMenuItem
                  data-testid="translation-link"
                  onClick={() => {
                    if (mention.translated) {
                      onRevertTranslation();
                    } else {
                      mutateTranslate();
                    }
                  }}
                >
                  <LanguagesIcon />
                  {mention.translated ? (
                    <Trans>Visa original</Trans>
                  ) : (
                    <Trans>Översätt</Trans>
                  )}
                </DropdownMenuItem>
              ) : null}

              {mention.sentiment && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    {SentimentIcon && <SentimentIcon />}
                    <Trans>Ändra sentiment</Trans>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent>
                      {Object.entries(SENTIMENT).map(
                        ([, { icon: Icon, label, value }]) => (
                          <DropdownMenuCheckboxItem
                            checked={value === mention.sentiment}
                            onClick={() => {
                              mutateSetSentiment(value);
                            }}
                            key={value}
                          >
                            <Icon />
                            {i18n._(label)}
                          </DropdownMenuCheckboxItem>
                        )
                      )}
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
              )}

              <AccessControl level={ACCESS_LEVELS.STAFF}>
                <DropdownMenuItem
                  render={
                    <a
                      target="_blank"
                      href={mention.admin_url}
                      data-testid="mention-admin-url"
                      rel="noopener noreferrer"
                    />
                  }
                >
                  <EyeIcon />
                  <Trans>Visa omnämnande i interna verktyget</Trans>
                  <StaffBadge />
                </DropdownMenuItem>
              </AccessControl>

              <DropdownMenuSeparator />

              <DropdownMenuItem
                variant="destructive"
                onClick={() => {
                  mutateMarkAsIrrelevant();
                }}
              >
                <Trash2Icon />
                <Trans>Ta bort omnämnande</Trans>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
